import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Select, Button, Row, Col, Space, Tooltip, App, Typography, type InputRef } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, FileTextOutlined, CheckCircleOutlined, StopOutlined, SearchOutlined } from '@ant-design/icons';
import { StandardCard, ExportExcelButton, ExportPDFButton, PageTitle, ColumnManagerDropdown } from '../common';
import { useColumnManager } from '../../hooks';
import { theme } from '../../theme';
import { apiClient } from '../../utils/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const { Text } = Typography;
const { Option } = Select;

interface Contrato {
  id: number;
  ncontrato: string;
  tipo: string;
  producto_nombre: string;
  fecha: string;
  comprador_nombre: string;
  vendedor_nombre: string;
  despachante_nombre: string;
  corredor_nombre: string;
  entregador_nombre: string;
  destinatario_nombre: string;
  empresa_nombre: string;
  estado: string;
  tarafardo: number;
  kilos: number;
  precio: number;
  fecha_contrato: string;
  fecha_cierre: string;
  unidades_aplicadas: number;
  unidades_entregadas_recibidas: number;
  [key: string]: any;
}

interface Producto {
  id: number;
  descripcion: string;
}

const ListadoContratos: React.FC = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [contratos, setContratos] = useState<Contrato[]>([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [searchText, setSearchText] = useState<Record<string, string>>({});
  const searchInput = useRef<InputRef>(null);
  const [globalSearch, setGlobalSearch] = useState<string>('');

  // Estados para la búsqueda
  const [productos, setProductos] = useState<Producto[]>([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState<number | null>(null);
  const [tipoSeleccionado, setTipoSeleccionado] = useState<string>('');
  const [contratosFiltrados, setContratosFiltrados] = useState<Contrato[]>([]);
  const [buscando, setBuscando] = useState(false);

  // Lista de todas las columnas disponibles
  const allColumnKeys = [
    'ncontrato',
    'tipo',
    'producto_nombre',
    'fecha',
    'comprador_nombre',
    'vendedor_nombre',
    'despachante_nombre',
    'corredor_nombre',
    'entregador_nombre',
    'destinatario_nombre',
    'estado',
    'tarafardo',
    'kilos',
    'precio',
    'fecha_contrato',
    'fecha_cierre',
    'unidades_aplicadas',
    'unidades_entregadas_recibidas',
    'pendiente',
    'acciones'
  ];

  const columnNames: Record<string, string> = {
    'ncontrato': 'Número',
    'tipo': 'Tipo',
    'producto_nombre': 'Producto',
    'fecha': 'Fecha',
    'comprador_nombre': 'Comprador',
    'vendedor_nombre': 'Vendedor',
    'despachante_nombre': 'Despachante',
    'corredor_nombre': 'Corredor',
    'entregador_nombre': 'Entregador',
    'destinatario_nombre': 'Destinatario',
    'estado': 'Estado',
    'tarafardo': 'Tara Fardo',
    'kilos': 'Kilos',
    'precio': 'Precio',
    'fecha_contrato': 'Fecha Contrato',
    'fecha_cierre': 'Fecha Cierre',
    'unidades_aplicadas': 'Aplicados',
    'unidades_entregadas_recibidas': 'Recibido/Entregado',
    'pendiente': 'Pendiente',
    'acciones': 'Acciones'
  };

  // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
  const columnManager = useColumnManager({
    storageKey: 'contratos',
    allKeys: allColumnKeys,
    requiredKeys: ['acciones'],
  });

  const handleNewClick = () => {
    navigate('/altacontratos');
  };

  const handleCierreContratoInline = async (record: Contrato) => {
    const confirmar = window.confirm(`¿Está seguro de cerrar el Contrato ${record.ncontrato}?`);
    if (!confirmar) return;

    try {
      const id_empresa = user?.id_empresa;
      if (!id_empresa) {
        message.error('No se encontró la empresa asociada');
        return;
      }

      await apiClient.post(`/ordenes/cerrar_contrato/${id_empresa}/${record.ncontrato}/`);

      if (contratosFiltrados.length > 0) {
        const updatedContratosFiltrados = contratosFiltrados.map(contrato =>
          contrato.ncontrato === record.ncontrato
            ? { ...contrato, estado: 'Cerrada' }
            : contrato
        );
        setContratosFiltrados(updatedContratosFiltrados);
      }
      if (contratos && Array.isArray(contratos)) {
        const updatedContratos = contratos.map(contrato =>
          contrato.ncontrato === record.ncontrato
            ? { ...contrato, estado: 'Cerrada' }
            : contrato
        );
        setContratos(updatedContratos);
      }

      message.success('Contrato cerrado con éxito');
    } catch (error) {
      console.error('Error al cerrar el contrato:', error);
      message.error('Error al cerrar el contrato');
    }
  };

  const cargarProductos = async () => {
    try {
      setCargando(true);
      const token = await getAccessTokenSilently();
      
      if (!process.env.REACT_APP_BALANZA_URL) {
        console.error('REACT_APP_BALANZA_URL no está definida');
        message.error('Error de configuración: URL de balanza no definida');
        return;
      }
      
      const url = `${process.env.REACT_APP_BALANZA_URL}/productos/`;
      console.log('Cargando productos desde:', url);
      
      const response = await apiClient.get<Producto[]>(url, { token });
      setProductos(response.data);
    } catch (error) {
      console.error('Error al cargar productos:', error);
      message.error('Error al cargar productos');
    } finally {
      setCargando(false);
    }
  };

  const realizarBusqueda = async () => {
    try {
      setBuscando(true);
      setContratosFiltrados([]);

      const id_empresa = user?.id_empresa;
      if (!id_empresa) {
        message.error('No se encontró la empresa asociada');
        return;
      }

      const params: any = {};
      if (productoSeleccionado) {
        params.producto_id = productoSeleccionado.toString();
      }
      if (tipoSeleccionado) {
        params.tipo = tipoSeleccionado;
      }

      if (!process.env.REACT_APP_ORDENES_URL) {
        message.error('Error de configuración: URL de órdenes no definida');
        return;
      }

      const token = await getAccessTokenSilently();
      const url = `${process.env.REACT_APP_ORDENES_URL}/listado_contratos/${id_empresa}/3/`;
      const response = await apiClient.get(url, { token, params });

      if (response.data.results && Array.isArray(response.data.results)) {
        setContratosFiltrados(response.data.results);
      } else if (Array.isArray(response.data)) {
        setContratosFiltrados(response.data);
      } else {
        setContratosFiltrados([]);
      }
    } catch (error) {
      console.error('Error al realizar búsqueda:', error);
      message.error('Error al realizar la búsqueda');
      setContratosFiltrados([]);
    } finally {
      setBuscando(false);
    }
  };

  const limpiarFiltros = () => {
    setProductoSeleccionado(null);
    setTipoSeleccionado('');
    setContratosFiltrados([]);
    setGlobalSearch('');
  };

  const handleSearch = (value: string, dataIndex: string) => {
    const newSearchText = { ...searchText, [dataIndex]: value };
    setSearchText(newSearchText);
  };

  // Función para resaltar términos en el texto
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} style={{
          backgroundColor: '#fff3cd',
          padding: '2px 4px',
          borderRadius: '3px',
          fontWeight: 600
        }}>
          {part}
        </mark>
      ) : part
    );
  };

  // Filtrar datos según búsqueda global
  const filteredData = useMemo(() => {
    const datosAConsiderar = contratosFiltrados.length > 0 ? contratosFiltrados : contratos;

    if (!Array.isArray(datosAConsiderar)) {
      return [];
    }

    let filtered = datosAConsiderar.filter(item => {
      return Object.keys(searchText).every(key => {
        if (!searchText[key]) return true;
        const itemValue = String(item[key] || '').toLowerCase();
        return itemValue.includes(searchText[key].toLowerCase());
      });
    });

    if (globalSearch.trim()) {
      const searchLower = globalSearch.toLowerCase();
      filtered = filtered.filter(record => {
        return (
          record.ncontrato?.toLowerCase().includes(searchLower) ||
          record.tipo?.toLowerCase().includes(searchLower) ||
          record.producto_nombre?.toLowerCase().includes(searchLower) ||
          record.comprador_nombre?.toLowerCase().includes(searchLower) ||
          record.vendedor_nombre?.toLowerCase().includes(searchLower) ||
          record.destinatario_nombre?.toLowerCase().includes(searchLower) ||
          record.estado?.toLowerCase().includes(searchLower)
        );
      });
    }

    return filtered;
  }, [contratosFiltrados, contratos, searchText, globalSearch]);

  // Calcular estadísticas
  const estadisticas = useMemo(() => {
    const activos = filteredData.filter(r => r.estado === 'Activa').length;
    const cerrados = filteredData.filter(r => r.estado === 'Cerrada').length;
    const bajas = filteredData.filter(r => r.estado === 'Baja').length;
    const compras = filteredData.filter(r => r.tipo === 'C').length;
    const ventas = filteredData.filter(r => r.tipo === 'V').length;

    const totalKilos = filteredData.reduce((acc, r) => acc + (r.kilos || 0), 0);
    const totalAplicados = filteredData.reduce((acc, r) => acc + (r.unidades_aplicadas || 0), 0);
    const totalRecibidos = filteredData.reduce((acc, r) => acc + (r.unidades_entregadas_recibidas || 0), 0);

    return {
      total: filteredData.length,
      activos,
      cerrados,
      bajas,
      compras,
      ventas,
      totalKilos,
      totalAplicados,
      totalRecibidos
    };
  }, [filteredData]);

  // Función para exportar a Excel
  const handleExportExcel = () => {
    try {
      const worksheet = XLSX.utils.json_to_sheet(
        filteredData.map(record => ({
          'Número': record.ncontrato,
          'Tipo': record.tipo === 'C' ? 'Compra' : 'Venta',
          'Producto': record.producto_nombre,
          'Fecha': record.fecha,
          'Comprador': record.comprador_nombre,
          'Vendedor': record.vendedor_nombre,
          'Destinatario': record.destinatario_nombre,
          'Estado': record.estado,
          'Kilos': record.kilos,
          'Precio': record.precio,
          'Aplicados': record.unidades_aplicadas || 0,
          'Recibidos': record.unidades_entregadas_recibidas || 0
        }))
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Contratos');
      XLSX.writeFile(workbook, `contratos_${new Date().toISOString().split('T')[0]}.xlsx`);
      message.success('Archivo Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      message.error('Error al exportar el archivo Excel');
    }
  };

  // Función para exportar a PDF
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text('Listado de Contratos', 14, 15);
      doc.setFontSize(10);
      doc.text(`Fecha: ${new Date().toLocaleDateString('es-AR')}`, 14, 22);
      doc.text(`Total de registros: ${filteredData.length}`, 14, 28);

      const tableData = filteredData.map(record => [
        record.ncontrato || '',
        record.tipo === 'C' ? 'Compra' : 'Venta',
        (record.producto_nombre || '').substring(0, 20),
        record.fecha || '',
        record.estado || ''
      ]);

      (doc as any).autoTable({
        head: [['Número', 'Tipo', 'Producto', 'Fecha', 'Estado']],
        body: tableData,
        startY: 35,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [45, 92, 146] }
      });

      doc.save(`contratos_${new Date().toISOString().split('T')[0]}.pdf`);
      message.success('Archivo PDF exportado correctamente');
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      message.error('Error al exportar el archivo PDF');
    }
  };

  const calcularTotales = () => {
    const totales = filteredData.reduce((acc, contrato) => {
      const aplicados = contrato.unidades_aplicadas || 0;
      const recibidos = contrato.unidades_entregadas_recibidas || 0;
      return {
        totalAplicados: acc.totalAplicados + aplicados,
        totalRecibidos: acc.totalRecibidos + recibidos,
        totalPendiente: acc.totalPendiente + (aplicados - recibidos)
      };
    }, { totalAplicados: 0, totalRecibidos: 0, totalPendiente: 0 });

    return totales;
  };

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Buscar ${columnNames[dataIndex] || dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => {
            setSelectedKeys(e.target.value ? [e.target.value] : []);
          }}
          onPressEnter={() => {
            handleSearch(selectedKeys[0], dataIndex);
            confirm();
          }}
          style={{
            display: 'block',
            width: 188,
            marginBottom: 8
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => {
              handleSearch(selectedKeys[0], dataIndex);
              confirm();
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
              backgroundColor: theme.colors.primary,
              borderColor: theme.colors.primary
            }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              handleSearch('', dataIndex);
            }}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? theme.colors.secondary : theme.colors.textSecondary }} />
    ),
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const allColumnsList: ColumnsType<Contrato> = [
    {
      title: 'Número',
      dataIndex: 'ncontrato',
      key: 'ncontrato',
      width: 100,
      align: 'right' as const,
      ...getColumnSearchProps('ncontrato'),
      render: (text: string) => globalSearch ? highlightText(text, globalSearch) : text,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      filters: [
        { text: 'Compra', value: 'C' },
        { text: 'Venta', value: 'V' },
      ],
      filteredValue: filteredInfo.tipo || null,
      onFilter: (value: any, record: Contrato) => record.tipo === value,
      render: (text: string) => {
        if (!text) return '';
        const displayText = text === 'C' ? 'Compra' : 'Venta';
        return globalSearch ? highlightText(displayText, globalSearch) : displayText;
      },
    },
    {
      title: 'Producto',
      dataIndex: 'producto_nombre',
      key: 'producto_nombre',
      width: 150,
      ellipsis: true,
      ...getColumnSearchProps('producto_nombre'),
      render: (text: string) => globalSearch ? highlightText(text || '', globalSearch) : text,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 100,
      render: (text: string) => globalSearch ? highlightText(text || '', globalSearch) : text,
    },
    {
      title: 'Comprador',
      dataIndex: 'comprador_nombre',
      key: 'comprador_nombre',
      width: 200,
      ellipsis: true,
      ...getColumnSearchProps('comprador_nombre'),
      render: (text: string) => globalSearch ? highlightText(text || '', globalSearch) : text,
    },
    {
      title: 'Vendedor',
      dataIndex: 'vendedor_nombre',
      key: 'vendedor_nombre',
      width: 200,
      ellipsis: true,
      ...getColumnSearchProps('vendedor_nombre'),
      render: (text: string) => globalSearch ? highlightText(text || '', globalSearch) : text,
    },
    {
      title: 'Despachante',
      dataIndex: 'despachante_nombre',
      key: 'despachante_nombre',
      width: 200,
      ellipsis: true,
      ...getColumnSearchProps('despachante_nombre'),
      render: (text: string) => globalSearch ? highlightText(text || '', globalSearch) : text,
    },
    {
      title: 'Corredor',
      dataIndex: 'corredor_nombre',
      key: 'corredor_nombre',
      width: 200,
      ellipsis: true,
      ...getColumnSearchProps('corredor_nombre'),
      render: (text: string) => globalSearch ? highlightText(text || '', globalSearch) : text,
    },
    {
      title: 'Entregador',
      dataIndex: 'entregador_nombre',
      key: 'entregador_nombre',
      width: 200,
      ellipsis: true,
      ...getColumnSearchProps('entregador_nombre'),
      render: (text: string) => globalSearch ? highlightText(text || '', globalSearch) : text,
    },
    {
      title: 'Destinatario',
      dataIndex: 'destinatario_nombre',
      key: 'destinatario_nombre',
      width: 200,
      ellipsis: true,
      ...getColumnSearchProps('destinatario_nombre'),
      render: (text: string) => globalSearch ? highlightText(text || '', globalSearch) : text,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 100,
      filters: [
        { text: 'Baja', value: 'Baja' },
        { text: 'Activa', value: 'Activa' },
        { text: 'Cerrada', value: 'Cerrada' },
      ],
      filteredValue: filteredInfo.estado || null,
      onFilter: (value: any, record: Contrato) => record.estado === value,
      render: (text: string) => globalSearch ? highlightText(text || '', globalSearch) : text,
    },
    {
      title: 'Tara Fardo',
      dataIndex: 'tarafardo',
      key: 'tarafardo',
      width: 100,
      align: 'right' as const,
      render: (value: number) => value ? value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
    },
    {
      title: 'Kilos',
      dataIndex: 'kilos',
      key: 'kilos',
      width: 100,
      align: 'right' as const,
      render: (value: number) => value ? value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
    },
    {
      title: 'Precio',
      dataIndex: 'precio',
      key: 'precio',
      width: 100,
      align: 'right' as const,
      render: (value: number) => value ? value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-',
    },
    {
      title: 'Fecha Contrato',
      dataIndex: 'fecha_contrato',
      key: 'fecha_contrato',
      width: 120,
    },
    {
      title: 'Fecha Cierre',
      dataIndex: 'fecha_cierre',
      key: 'fecha_cierre',
      width: 120,
    },
    {
      title: 'Aplicados',
      dataIndex: 'unidades_aplicadas',
      key: 'unidades_aplicadas',
      width: 100,
      align: 'right' as const,
      render: (value: number) => (value || 0).toLocaleString('es-AR'),
    },
    {
      title: 'Recibido/Entregado',
      dataIndex: 'unidades_entregadas_recibidas',
      key: 'unidades_entregadas_recibidas',
      width: 150,
      align: 'right' as const,
      render: (value: number) => (value || 0).toLocaleString('es-AR'),
    },
    {
      title: 'Pendiente',
      key: 'pendiente',
      width: 100,
      align: 'right' as const,
      render: (_: any, record: Contrato) => {
        const aplicados = record.unidades_aplicadas || 0;
        const recibidos = record.unidades_entregadas_recibidas || 0;
        const pendiente = aplicados - recibidos;
        return (
          <span style={{ 
            color: pendiente > 0 ? theme.colors.warning : pendiente < 0 ? theme.colors.error : theme.colors.textSecondary,
            fontWeight: pendiente !== 0 ? 600 : 400
          }}>
            {pendiente.toLocaleString('es-AR')}
          </span>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: Contrato) => {
        if (record.ncontrato === 'TOTALES') return null;

        const isActive = record.estado === 'Activa';
        const isClosed = record.estado === 'Cerrada';
        const isBaja = record.estado === 'Baja';
        // Emisión está habilitada para Activa, Cerrada o Baja (igual que en main)
        const isEmissionEnabled = isActive || isClosed || isBaja;

        return (
          <Space size="small">
            <Tooltip title={isActive ? "Aplicar Fardos" : "Solo disponible para contratos activos"}>
              <Button
                icon={<CheckCircleOutlined />}
                size="middle"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/altaaplicacion', { state: { id_contrato: record.id, ncontrato: record.ncontrato } });
                }}
                disabled={!isActive}
                style={{
                  backgroundColor: isActive ? theme.colors.info : undefined,
                  borderColor: isActive ? theme.colors.info : undefined,
                  color: isActive ? 'white' : undefined,
                  fontSize: '13px',
                  height: '32px',
                  minWidth: '32px',
                  padding: '4px 8px'
                }}
              />
            </Tooltip>
            <Tooltip title={isEmissionEnabled ? "Emitir" : "No disponible para este estado"}>
              <Button
                icon={<FileTextOutlined />}
                size="middle"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/emisioncontratos', { state: { id_contrato: record.id } });
                }}
                disabled={!isEmissionEnabled}
                style={{
                  backgroundColor: isEmissionEnabled ? theme.colors.secondary : undefined,
                  borderColor: isEmissionEnabled ? theme.colors.secondary : undefined,
                  color: isEmissionEnabled ? 'white' : undefined,
                  fontSize: '13px',
                  height: '32px',
                  minWidth: '32px',
                  padding: '4px 8px'
                }}
              >
                Emitir
              </Button>
            </Tooltip>
            <Tooltip title={isActive ? "Cerrar Contrato" : "Solo disponible para contratos activos"}>
              <Button
                icon={<StopOutlined />}
                size="middle"
                danger
                onClick={(e) => {
                  e.stopPropagation();
                  handleCierreContratoInline(record);
                }}
                disabled={!isActive}
                style={{
                  fontSize: '13px',
                  height: '32px',
                  minWidth: '32px',
                  padding: '4px 8px'
                }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
  const columnsByKey = new Map(allColumnsList.map(col => [col.key as string, col]));
  const columns: ColumnsType<Contrato> = columnManager.orderedVisibleKeys
    .map(key => columnsByKey.get(key))
    .filter((col): col is (typeof allColumnsList)[number] => Boolean(col));

  useEffect(() => {
    if (isAuthenticated && user) {
      cargarProductos();
    }
  }, [isAuthenticated, user]);

  if (error) return (
    <div style={{
      backgroundColor: '#fff3cd',
      border: '1px solid #ffeeba',
      color: '#856404',
      padding: '1rem',
      marginTop: '1rem',
      textAlign: 'center'
    }}>
      <h4>¡Atención!</h4>
      <p>{error}</p>
      {!isAuthenticated && (
        <p>Por favor, inicie sesión para continuar.</p>
      )}
    </div>
  );

  return (
    <div style={{ padding: '0' }}>
      {/* Título y estadísticas compactas */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <PageTitle level={1} style={{ fontSize: '22px', marginBottom: 0 }}>
          Listado de Contratos
        </PageTitle>

        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Estadísticas compactas */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 8px',
            backgroundColor: theme.colors.backgroundGray,
            borderRadius: '4px',
            border: `1px solid ${theme.colors.borderLight}`
          }}>
            <Text type="secondary" style={{ fontSize: '10px' }}>Total:</Text>
            <Text strong style={{ fontSize: '12px', color: theme.colors.textPrimary }}>
              {estadisticas.total}
            </Text>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 8px',
            backgroundColor: '#f6ffed',
            borderRadius: '4px',
            border: `1px solid #b7eb8f`
          }}>
            <Text type="secondary" style={{ fontSize: '10px' }}>Activos:</Text>
            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>
              {estadisticas.activos}
            </Text>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 8px',
            backgroundColor: '#fff1f0',
            borderRadius: '4px',
            border: `1px solid #ffccc7`
          }}>
            <Text type="secondary" style={{ fontSize: '10px' }}>Cerrados:</Text>
            <Text strong style={{ fontSize: '12px', color: '#ff4d4f' }}>
              {estadisticas.cerrados}
            </Text>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 8px',
            backgroundColor: '#e6f7ff',
            borderRadius: '4px',
            border: `1px solid #91d5ff`
          }}>
            <Text type="secondary" style={{ fontSize: '10px' }}>Kilos:</Text>
            <Text strong style={{ fontSize: '12px', color: '#1890ff' }}>
              {estadisticas.totalKilos.toLocaleString('es-AR')}
            </Text>
          </div>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleNewClick}
            size="middle"
            style={{ 
              backgroundColor: theme.colors.primary, 
              borderColor: theme.colors.primary,
              height: '32px',
              fontSize: '13px',
              minWidth: '100px'
            }}
          >
            Nuevo
          </Button>
        </div>
      </div>

      {/* Búsqueda global */}
      <StandardCard 
        title="Búsqueda Global" 
        style={{ marginBottom: '12px' }}
        headStyle={{
          padding: '8px 16px',
          fontSize: '14px'
        }}
        bodyStyle={{ padding: '12px 16px' }}
      >
        <Input.Search
          placeholder="Buscar en todos los campos (número, tipo, producto, comprador, vendedor, estado...)"
          allowClear
          prefix={<SearchOutlined style={{ color: theme.colors.textSecondary }} />}
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          size="middle"
          style={{ 
            width: '100%',
            maxWidth: '100%',
            fontSize: '13px'
          }}
        />
      </StandardCard>

      {/* Filtros y controles */}
      <StandardCard
        title="Filtros de Búsqueda"
        style={{ marginBottom: '12px' }}
        headStyle={{
          padding: '8px 16px',
          fontSize: '14px'
        }}
        bodyStyle={{ padding: '12px 16px' }}
        extra={
          <Space size="small">
            <ColumnManagerDropdown
              manager={columnManager}
              labels={columnNames}
              requiredKeys={['acciones']}
              buttonSize="middle"
            />
            <ExportExcelButton
              size="middle"
              onClick={handleExportExcel}
              disabled={filteredData.length === 0}
            />
            <ExportPDFButton
              size="middle"
              onClick={handleExportPDF}
              disabled={filteredData.length === 0}
            />
          </Space>
        }
      >
        {/* Filtros de búsqueda */}
        <Row gutter={[12, 12]} align="bottom" style={{ marginBottom: '12px' }}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 500, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0
              }}>
                Producto:
              </label>
              <Select
                placeholder="Seleccione un producto"
                value={productoSeleccionado}
                onChange={setProductoSeleccionado}
                style={{ width: '100%', fontSize: '13px' }}
                allowClear
                size="middle"
                showSearch
                optionFilterProp="children"
                filterOption={(input: string, option?: { children: string; value: number }) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase()) ?? false
                }
              >
                {productos.map(producto => (
                  <Option key={producto.id} value={producto.id}>
                    {producto.descripcion}
                  </Option>
                ))}
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ 
                fontWeight: 500, 
                color: theme.colors.textPrimary,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                marginBottom: 0
              }}>
                Tipo:
              </label>
              <Select
                placeholder="Seleccione tipo"
                value={tipoSeleccionado}
                onChange={setTipoSeleccionado}
                style={{ width: '100%', fontSize: '13px' }}
                allowClear
                size="middle"
              >
                <Option value="C">Compra</Option>
                <Option value="V">Venta</Option>
              </Select>
            </div>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-end', 
              justifyContent: 'flex-end', 
              height: '100%', 
              paddingBottom: '4px', 
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <Button
                onClick={limpiarFiltros}
                size="middle"
                style={{ 
                  fontSize: '13px',
                  minWidth: '100px',
                  height: '32px'
                }}
              >
                Limpiar
              </Button>
              <Button
                type="primary"
                onClick={realizarBusqueda}
                loading={buscando}
                size="middle"
                icon={<SearchOutlined />}
                style={{ 
                  fontSize: '13px',
                  minWidth: '100px',
                  height: '32px'
                }}
              >
                Buscar
              </Button>
            </div>
          </Col>
        </Row>

        <Table<Contrato>
          dataSource={[...filteredData, {
            ncontrato: 'TOTALES',
            tipo: '',
            producto_nombre: '',
            fecha: '',
            comprador_nombre: '',
            vendedor_nombre: '',
            despachante_nombre: '',
            corredor_nombre: '',
            entregador_nombre: '',
            destinatario_nombre: '',
            empresa_nombre: '',
            estado: '',
            tarafardo: 0,
            kilos: 0,
            precio: 0,
            fecha_contrato: '',
            fecha_cierre: '',
            unidades_aplicadas: calcularTotales().totalAplicados,
            unidades_entregadas_recibidas: calcularTotales().totalRecibidos,
            id: -1
          }]}
          columns={columns}
          loading={cargando}
          rowKey={(record) => record.ncontrato === 'TOTALES' ? 'totales' : record.ncontrato}
          scroll={{ x: 2230, y: 'max(150px, calc(100vh - 500px))' }}
          pagination={{
            pageSize: 50,
            position: ['bottomRight'],
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`
          }}
          size="middle"
          bordered
          onChange={(pagination, filters) => {
            setFilteredInfo(filters);
          }}
          onRow={(record) => ({
            style: record.ncontrato === 'TOTALES'
              ? { backgroundColor: '#f0f8ff', fontWeight: 'bold' }
              : record.estado === 'Cerrada'
                ? { backgroundColor: '#fff1f0' }
                : undefined
          })}
          rowClassName={(record) =>
            record.ncontrato === 'TOTALES' ? 'totales-row' : ''
          }
          locale={{
            emptyText: filteredData.length === 0 && contratosFiltrados.length === 0 
              ? 'No hay contratos. Use los filtros para buscar contratos.'
              : 'No se encontraron resultados con los filtros aplicados.'
          }}
        />
      </StandardCard>
    </div>
  );
};

// Estilos CSS personalizados para la fila de totales y botones de exportación
const totalesStyles = `
  .totales-row {
    background-color: #f0f8ff !important;
    font-weight: bold !important;
  }
  .totales-row td {
    background-color: #f0f8ff !important;
    font-weight: bold !important;
  }
  .export-excel-btn:not(:disabled) {
    color: #ffffff !important;
    background-color: #52c41a !important;
    border-color: #52c41a !important;
  }
  .export-excel-btn:not(:disabled) .anticon {
    color: #ffffff !important;
  }
  .export-excel-btn:not(:disabled) span {
    color: #ffffff !important;
  }
  .export-pdf-btn:not(:disabled) {
    color: #ffffff !important;
    background-color: #ff4d4f !important;
    border-color: #ff4d4f !important;
  }
  .export-pdf-btn:not(:disabled) .anticon {
    color: #ffffff !important;
  }
  .export-pdf-btn:not(:disabled) span {
    color: #ffffff !important;
  }
`;

// Agregar estilos al documento
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = totalesStyles;
  document.head.appendChild(styleElement);
}

export default ListadoContratos;
